// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ProcessMapOrbital
// Orbital layout for Level 1 process map (PE arc / PO line / PS arc)
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useMemo, type CSSProperties } from 'react';
import { PM } from '../../../../../config/processMapStyles';
import { useI18n } from '../../../../../i18n';
import { useProcessNavigation } from '../hooks/useProcessNavigation';
import {
  IconGobierno,
  IconStructure,
  IconProcess,
  IconInformation,
  IconCompliance,
  IconTech,
  IconReputation,
  IconDesarrolloComercial,
  IconPropuestaContratacion,
  IconBrujula,
  IconLibro,
  IconCalculadora,
  IconTalento,
  IconServidor,
  IconMegafono,
  IconBalanza,
} from './ProcessMapIcons';
import type { TipologiaGroup, ProcessGroup } from '../types/process-map.types';

// ─── Icon map by grupoNumero ────────────────────────────

type IconFC = React.FC<{ size?: number; color?: string }>;

// PO icon map (grupoNumero 1-8 for PO)
const PO_ICON_MAP: Record<number, IconFC> = {
  1: IconDesarrolloComercial,
  2: IconPropuestaContratacion,
  3: IconStructure,
  4: IconProcess,
  5: IconInformation,
  6: IconCompliance,
  7: IconTech,
  8: IconReputation,
};

// Additional icons for PE/PS groups when they don't appear in the standard map
const PE_ICON_MAP: Record<number, IconFC> = {
  1: IconGobierno, // Gobierno Corporativo — templo/columnas
  2: IconBrujula, // Planificacion Estrategica — brujula
  3: IconLibro, // Conocimiento Corporativo — libro abierto
};

const PS_ICON_MAP: Record<number, IconFC> = {
  1: IconCalculadora, // PS Gr.1 Administracion y Finanzas — calculadora
  2: IconTalento, // PS Gr.2 Personas — persona+check
  3: IconServidor, // PS Gr.3 Tecnologia y Sistemas — servidor
  4: IconMegafono, // PS Gr.4 Comunicacion y Conocimiento — megafono
  5: IconBalanza, // PS Gr.5 Cumplimiento Normativo — balanza
};

// ─── Positioning math ───────────────────────────────────

function positionOnArc(centerX: number, centerY: number, radius: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(rad),
    y: centerY - radius * Math.sin(rad),
  };
}

// ─── Component ──────────────────────────────────────────

interface ProcessMapOrbitalProps {
  tipologias: TipologiaGroup[];
}

export function ProcessMapOrbital({ tipologias }: ProcessMapOrbitalProps) {
  const { t } = useI18n();
  const { goToLevel2 } = useProcessNavigation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [containerHeight, setContainerHeight] = useState(600);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const peGroup = tipologias.find((t) => t.tipologia === 'PE');
  const poGroup = tipologias.find((t) => t.tipologia === 'PO');
  const psGroup = tipologias.find((t) => t.tipologia === 'PS');

  const centerX = containerWidth / 2;
  const centerY = containerHeight / 2;

  // Donut radius proportional to both dimensions
  const maxRadiusByWidth = containerWidth * 0.35;
  const maxRadiusByHeight = containerHeight * 0.38;
  const donutRadius = Math.min(maxRadiusByWidth, maxRadiusByHeight);

  // Imagotipo size proportional to donut
  const imagotipoSize = donutRadius * 2.25;

  // Block D — Memoize PE positions
  const peAngles = [135, 90, 45];
  const pePositions = useMemo(() => {
    return peAngles.map((angle) => positionOnArc(centerX, centerY, donutRadius, angle));
  }, [centerX, centerY, donutRadius]);

  // Block D — Memoize PS positions
  const psAngles = [-150, -120, -90, -60, -30];
  const psPositions = useMemo(() => {
    return psAngles.map((angle) => positionOnArc(centerX, centerY, donutRadius, angle));
  }, [centerX, centerY, donutRadius]);

  // Block A — Compute vertical centroids for left margin labels
  const peLabel =
    `PE \u2014 ${(t.typology as Record<string, string>).PE ?? 'Procesos Estrat\u00e9gicos'}`.toUpperCase();
  const poLabel =
    `PO \u2014 ${(t.typology as Record<string, string>).PO ?? 'Procesos Operativos'}`.toUpperCase();
  const psLabel =
    `PS \u2014 ${(t.typology as Record<string, string>).PS ?? 'Procesos de Soporte'}`.toUpperCase();

  const peCentroidY = useMemo(() => {
    if (pePositions.length === 0) return centerY * 0.3;
    return pePositions.reduce((sum, p) => sum + p.y, 0) / pePositions.length;
  }, [pePositions, centerY]);

  const psCentroidY = useMemo(() => {
    if (psPositions.length === 0) return centerY * 1.7;
    return psPositions.reduce((sum, p) => sum + p.y, 0) / psPositions.length;
  }, [psPositions, centerY]);

  const typologyLabelStyle: CSSProperties = {
    position: 'absolute',
    left: 16,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: 'rgba(31,56,100, 0.25)',
    zIndex: 1,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        height: '100%',
        minHeight: 450,
        background: PM.surface,
        overflow: 'hidden',
        borderRadius: PM.card.borderRadius,
        border: PM.card.border,
        boxShadow: PM.card.shadow,
      }}
    >
      {/* Block A — Left margin typology labels */}
      {peGroup && <div style={{ ...typologyLabelStyle, top: peCentroidY - 7 }}>{peLabel}</div>}
      {poGroup && <div style={{ ...typologyLabelStyle, top: centerY - 7 }}>{poLabel}</div>}
      {psGroup && <div style={{ ...typologyLabelStyle, top: psCentroidY - 7 }}>{psLabel}</div>}

      {/* B. Center watermark — dual layer (base blur + top detail) */}
      <img
        src="/img/Imagotipo-Color.png"
        alt=""
        style={{
          width: imagotipoSize,
          height: imagotipoSize,
          objectFit: 'contain',
          opacity: 0.05,
          filter: 'blur(4px)',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <img
        src="/img/Imagotipo-Color.png"
        alt=""
        style={{
          width: imagotipoSize,
          height: imagotipoSize,
          objectFit: 'contain',
          opacity: 0.08,
          filter: 'blur(1px)',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* C. Decorative orbit ellipses */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <ellipse
          cx={centerX}
          cy={centerY}
          rx={donutRadius}
          ry={donutRadius * 0.55}
          fill="none"
          stroke={PM.orbital.orbitStroke}
          strokeWidth="1"
          strokeDasharray="6 4"
        />
        <ellipse
          cx={centerX}
          cy={centerY}
          rx={donutRadius * 1.15}
          ry={donutRadius * 1.15 * 0.55}
          fill="none"
          stroke={PM.orbital.orbitStroke}
          strokeWidth="1"
          strokeDasharray="6 4"
        />
      </svg>

      {/* D. PE arc — top semicircle, icons on donut border */}
      {peGroup && (
        <PEArc
          groups={peGroup.groups}
          centerX={centerX}
          centerY={centerY}
          donutRadius={donutRadius}
          onGroupClick={(gn) => goToLevel2(gn, 'PE')}
        />
      )}

      {/* E. PO horizontal line */}
      {poGroup && (
        <POLine
          groups={poGroup.groups}
          centerY={centerY}
          containerWidth={containerWidth}
          donutRadius={donutRadius}
          onGroupClick={(gn) => goToLevel2(gn, 'PO')}
        />
      )}

      {/* F. PS arc — bottom semicircle, icons on donut border */}
      {psGroup && (
        <PSArc
          groups={psGroup.groups}
          centerX={centerX}
          centerY={centerY}
          donutRadius={donutRadius}
          onGroupClick={(gn) => goToLevel2(gn, 'PS')}
        />
      )}

      {/* No processes fallback */}
      {tipologias.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: PM.textMut,
            fontSize: 14,
            zIndex: 1,
          }}
        >
          {t.processMap.sinProcesosRegistrados}
        </div>
      )}
    </div>
  );
}

// ─── PE Arc ─────────────────────────────────────────────

function PEArc({
  groups,
  centerX,
  centerY,
  donutRadius,
  onGroupClick,
}: {
  groups: ProcessGroup[];
  centerX: number;
  centerY: number;
  donutRadius: number;
  onGroupClick: (gn: number) => void;
}) {
  const angles = [135, 90, 45];
  const radius = donutRadius;
  const iconSize = PM.orbital.iconSizePE;

  return (
    <>
      {/* Group icons */}
      {groups.slice(0, angles.length).map((g, idx) => {
        const angle = angles[idx] ?? -90;
        const pos = positionOnArc(centerX, centerY, radius, angle);
        const Icon = PE_ICON_MAP[g.grupoNumero] ?? IconGobierno;

        return (
          <OrbitalIconButton
            key={g.grupoNumero}
            group={g}
            Icon={Icon}
            iconSize={iconSize}
            x={pos.x}
            y={pos.y}
            bgColor="#FFFFFF"
            borderColor={`1.5px solid ${PM.azul1}`}
            iconColor={PM.azul1}
            onClick={() => onGroupClick(g.grupoNumero)}
            labelPosition="above"
          />
        );
      })}
    </>
  );
}

// ─── PO Line ────────────────────────────────────────────

function POLine({
  groups,
  centerY,
  containerWidth,
  donutRadius,
  onGroupClick,
}: {
  groups: ProcessGroup[];
  centerY: number;
  containerWidth: number;
  donutRadius: number;
  onGroupClick: (gn: number) => void;
}) {
  const iconSize = PM.orbital.iconSizePO;
  const sorted = [...groups].sort((a, b) => a.grupoNumero - b.grupoNumero);

  // Uniform distribution — row limited to donut proportions
  const rowWidth = Math.min(containerWidth * 0.8, donutRadius * 3.2);
  const margin = (containerWidth - rowWidth) / 2;
  const usableWidth = rowWidth;
  const count = sorted.length;
  const slotWidth = count > 1 ? usableWidth / (count - 1) : 0;

  return (
    <>
      {/* Horizontal gradient line */}
      <div
        style={{
          position: 'absolute',
          left: margin,
          right: margin,
          top: centerY,
          height: 1.5,
          background: `linear-gradient(90deg, transparent, ${PM.orbital.poLineColor} 15%, ${PM.orbital.poLineColor} 85%, transparent)`,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* PO icons — uniform distribution (Block B: no brand rendering) */}
      {sorted.map((g, idx) => {
        const x = count > 1 ? margin + idx * slotWidth : containerWidth / 2;
        const Icon = PO_ICON_MAP[g.grupoNumero] ?? IconProcess;
        return (
          <OrbitalIconButton
            key={g.grupoNumero}
            group={g}
            Icon={Icon}
            iconSize={iconSize}
            x={x}
            y={centerY}
            bgColor={PM.azul1}
            borderColor={PM.azul1}
            iconColor="#FFFFFF"
            onClick={() => onGroupClick(g.grupoNumero)}
          />
        );
      })}

      {/* Chevron arrows between consecutive PO icons */}
      {sorted.length > 1 &&
        sorted.slice(0, -1).map((_, idx) => {
          const x1 = margin + idx * slotWidth;
          const x2 = margin + (idx + 1) * slotWidth;
          const gapStart = x1 + iconSize / 2 + 4;
          const gapEnd = x2 - iconSize / 2 - 4;
          const gapWidth = Math.max(gapEnd - gapStart, 12);
          const gradId = `arrowGrad-${idx}`;

          return (
            <svg
              key={`arrow-${idx}`}
              width={gapWidth}
              height={32}
              viewBox={`0 0 ${gapWidth} 32`}
              style={{
                position: 'absolute',
                left: gapStart,
                top: centerY - 16,
                zIndex: 2,
                pointerEvents: 'none',
                flexShrink: 0,
              }}
            >
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#D6E4F0" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#33BCE1" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <path
                d={`M0,4 L${gapWidth - 6},4 L${gapWidth},16 L${gapWidth - 6},28 L0,28 L6,16 Z`}
                fill={`url(#${gradId})`}
              />
            </svg>
          );
        })}
    </>
  );
}

// ─── PS Arc ─────────────────────────────────────────────

function PSArc({
  groups,
  centerX,
  centerY,
  donutRadius,
  onGroupClick,
}: {
  groups: ProcessGroup[];
  centerX: number;
  centerY: number;
  donutRadius: number;
  onGroupClick: (gn: number) => void;
}) {
  const angles = [-150, -120, -90, -60, -30];
  const radius = donutRadius;
  const iconSize = PM.orbital.iconSizePS;

  return (
    <>
      {/* Group icons */}
      {groups.slice(0, angles.length).map((g, idx) => {
        const angle = angles[idx] ?? 90;
        const pos = positionOnArc(centerX, centerY, radius, angle);
        const Icon = PS_ICON_MAP[g.grupoNumero] ?? IconBalanza;

        return (
          <OrbitalIconButton
            key={g.grupoNumero}
            group={g}
            Icon={Icon}
            iconSize={iconSize}
            x={pos.x}
            y={pos.y}
            bgColor={PM.celeste}
            borderColor="none"
            iconColor="#FFFFFF"
            onClick={() => onGroupClick(g.grupoNumero)}
            labelPosition="below"
          />
        );
      })}
    </>
  );
}

// ─── Orbital Icon Button ────────────────────────────────

function OrbitalIconButton({
  group,
  Icon,
  iconSize,
  x,
  y,
  bgColor,
  borderColor,
  iconColor,
  onClick,
  brand,
  labelPosition = 'below',
}: {
  group: ProcessGroup;
  Icon: IconFC;
  iconSize: number;
  x: number;
  y: number;
  bgColor: string;
  borderColor: string;
  iconColor: string;
  onClick: () => void;
  brand?: string;
  labelPosition?: 'above' | 'below';
}) {
  const [hovered, setHovered] = useState(false);

  const circleStyle: CSSProperties = {
    position: 'absolute',
    left: x - iconSize / 2,
    top: y - iconSize / 2,
    width: iconSize,
    height: iconSize,
    borderRadius: '50%',
    background: bgColor,
    border: `1.5px solid ${hovered ? iconColor : borderColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transform: hovered ? 'scale(1.08)' : 'scale(1)',
    boxShadow: hovered ? `0 6px 20px rgba(0,0,0,0.12)` : 'none',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const labelTop = labelPosition === 'above' ? y - iconSize / 2 - 34 : y + iconSize / 2 + 8;

  const labelStyle: CSSProperties = {
    position: 'absolute',
    left: x - 85,
    top: labelTop,
    width: 170,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: 600,
    color: PM.text,
    lineHeight: 1.3,
    zIndex: 2,
    pointerEvents: 'none',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  };

  const brandTop = labelPosition === 'above' ? y - iconSize / 2 - 48 : y + iconSize / 2 + 34;

  return (
    <>
      <div
        style={circleStyle}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Icon size={iconSize * 0.45} color={iconColor} />
      </div>
      <div style={labelStyle}>{group.grupoNombre}</div>
      {brand && (
        <div
          style={{
            position: 'absolute',
            left: x - 85,
            top: brandTop,
            width: 170,
            textAlign: 'center',
            fontSize: 12,
            fontStyle: 'italic',
            fontWeight: 500,
            color: PM.textMut,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {brand}
        </div>
      )}
    </>
  );
}
